import { Building2, ChevronDown } from 'lucide-react';
import { useEffect } from 'react';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import {
  isProjectVersion,
  PROJECT_STATUS_LABEL,
  type Project,
} from '@obraplan/core/domain/structure/project';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';
import { groupProjectFamilies } from './project-families';

/**
 * "Residencial Vienna · Ativa" e, para a versão de orçamento, a mesma linha com "· versão 2"
 * no fim: a versão fica marcada, e vem logo abaixo da aprovada na lista.
 */
function optionLabel(project: Project): string {
  const base = `${project.name} · ${PROJECT_STATUS_LABEL[project.status]}`;
  if (!isProjectVersion(project)) return base;
  return project.versionNumber === undefined
    ? `${base} · versão`
    : `${base} · versão ${String(project.versionNumber)}`;
}

/**
 * Obra em contexto.
 *
 * A seleção é conveniência de navegação — o escopo real é verificado em cada caso de uso
 * (RN-132). Lista apenas as obras que o perfil alcança: papel global vê todas, papel por
 * obra vê as suas.
 *
 * Obra arquivada não aparece aqui: quem arquivou disse que ela saiu do dia a dia, e
 * deixá-la a um clique de distância convida a lançar dado nela por engano. Ela continua
 * visível e editável na tela **Obras**, que é onde se desarquiva.
 *
 * ## A obra que não existe mais
 *
 * A obra ativa mora **no aparelho**, e a lista vem do servidor. Quando o id guardado não
 * está na lista — obra apagada, acesso revogado, ou um banco que não é o mesmo de ontem —
 * o seletor mostra "Selecione uma obra…" e, apesar disso, as telas continuam chamando os
 * casos de uso com aquele id: `SetActiveProject` valida acesso, não existência (RN-132),
 * e o administrador alcança tudo. O erro só aparece no fim, quando o banco recusa a
 * chave estrangeira. Some-se com o id assim que a lista chega: a tela passa a dizer
 * "nenhuma obra selecionada", que é a verdade, e o convite é escolher uma.
 */
export function ProjectSelector() {
  const container = useContainer();
  const { session, activeProjectId, selectProject } = useSessionState();

  const allProjects = useLive(
    () =>
      session === undefined
        ? Promise.resolve([])
        : container.listAccessibleProjects.execute(session),
    [session?.userId],
  );

  // Efeito antes de qualquer saída antecipada: a ordem dos hooks é fixa.
  useEffect(() => {
    if (allProjects === undefined || activeProjectId === undefined) return;
    if (allProjects.some((project) => project.id === activeProjectId)) return;
    void selectProject(undefined);
  }, [allProjects, activeProjectId, selectProject]);

  if (session === undefined) return null;

  if (allProjects === undefined) {
    return (
      <div className="h-11 w-full animate-pulse rounded-md bg-(--color-surface-muted) md:h-9 md:w-56" />
    );
  }

  // A obra ativa permanece na lista mesmo arquivada, senão o seletor mostraria vazio
  // enquanto a tela ainda exibe o conteúdo dela.
  const projects = allProjects.filter(
    (project) => project.status !== 'ARCHIVED' || project.id === activeProjectId,
  );

  if (projects.length === 0) {
    return <span className="text-sm text-(--color-content-muted)">Nenhuma obra acessível</span>;
  }

  return (
    <div className="relative flex w-full items-center md:w-auto">
      <Building2
        className="pointer-events-none absolute left-2.5 size-4 text-(--color-content-muted)"
        aria-hidden="true"
      />
      <select
        aria-label="Obra ativa"
        /*
         * `min-w-56` sem limite superior estourava a largura recebida no celular: o
         * `select` passava por baixo do bloco do perfil e a seta — que é posicionada em
         * relação a **este** contêiner — ia parar colada no botão do menu. A largura
         * mínima só existe onde há espaço para ela.
         */
        className="h-11 w-full appearance-none rounded-md border border-(--color-border-subtle) bg-(--color-surface) py-1 pl-8 pr-8 text-sm md:h-9 md:min-w-56"
        value={activeProjectId ?? ''}
        onChange={(event) => {
          const value = event.target.value;
          void selectProject(value === '' ? undefined : (value as ProjectId));
        }}
      >
        <option value="">Selecione uma obra…</option>
        {groupProjectFamilies(projects).flatMap((family) =>
          [family.root, ...family.versions].map((project) => (
            <option key={project.id} value={project.id}>
              {optionLabel(project)}
            </option>
          )),
        )}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2.5 size-4 text-(--color-content-muted)"
        aria-hidden="true"
      />
    </div>
  );
}
